import { useState, useEffect, MutableRefObject, useRef } from "react";
import Sketch from "@arcgis/core/widgets/Sketch";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import _ from "lodash";
import { getHoverSymbolForGeometryType, getSymbolForGeometryType } from "../../utils/arcgisHelper";
import { SketchTool } from "../editor/useSketchEvents";
import { graphicAdhocIndex } from "../../components/FeatureSelectorModal";
import { incrementName } from "../../../../utils/incrementName";

export interface FeatureTracking {
  [graphicId: string]: {
    featureId?: number;
    isNew?: boolean;
    isModified?: boolean;
    isDeleted?: boolean;
    originalGeometry?: any;
  };
}

interface UseAdhocSketchEventsOptions {
  sketchRef: MutableRefObject<Sketch | null>;
  sketchInitialized: boolean;
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  markFeatureAsChanged: ((graphicId: string) => void) | null;
  setSelectedFeatures: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  initialSelectionLoaded: boolean;
}

export function useAdhocSketchEvents({
  sketchRef,
  sketchInitialized,
  editLayerRef,
  markFeatureAsChanged,
  setSelectedFeatures,
  initialSelectionLoaded,
}: UseAdhocSketchEventsOptions) {
  const [featureTracking, setFeatureTracking] = useState<FeatureTracking>({});
  const [selectedTool, setSelectedTool] = useState<SketchTool>(SketchTool.Edit);

  const [undoableActions, setUndoableActions] = useState(0);
  const [redoableActions, setRedoableActions] = useState(0);
  const maxFeatureIndex = useRef(0);

  type SketchEventHandler = { remove: () => void } | null;
  const [sketchHandlers, setSketchHandlers] = useState<{
    update: SketchEventHandler;
    create: SketchEventHandler;
    delete: SketchEventHandler;
    updateSelection: SketchEventHandler;
    undo: SketchEventHandler;
    redo: SketchEventHandler;
  }>({
    update: null,
    create: null,
    delete: null,
    updateSelection: null,
    undo: null,
    redo: null,
  });

  // Clear and rebuild the index
  useEffect(() => {
    if (!editLayerRef.current) return;
    graphicAdhocIndex.clear();
    const adhocGraphics = editLayerRef.current.graphics.toArray();
    adhocGraphics.forEach((graphic, index) => {
      const graphicId = graphic.attributes?.graphicId;
      if (graphicId?.startsWith("adhoc-")) graphicAdhocIndex.set(graphicId, index);
    });
  }, [editLayerRef.current?.graphics.length]);

  const readdHandlers = (selectedTool: SketchTool) => {
    if (sketchRef.current == null) return [];

    _.values(sketchHandlers).forEach((handler) => handler?.remove());

    const updateHandler = sketchRef.current.on("update", handleSketchUpdate);
    const createHandler = sketchRef.current.on("create", handleSketchCreate);
    const deleteHandler = sketchRef.current.on("delete", handleSketchDelete);
    const updateSelectionHandler = sketchRef.current.on("update", handleSketchUpdateSelection(selectedTool));
    const undoHandler = sketchRef.current.on("undo", handleSketchUndo);
    const redoHandler = sketchRef.current.on("redo", handleSketchRedo);

    setSketchHandlers({
      update: updateHandler,
      create: createHandler,
      delete: deleteHandler,
      updateSelection: updateSelectionHandler,
      undo: undoHandler,
      redo: redoHandler,
    });

    return [updateHandler, createHandler, deleteHandler, updateSelectionHandler];
  };

  useEffect(() => {
    const handlers = readdHandlers(selectedTool);

    return () => {
      handlers.forEach((handler) => handler?.remove());
    };
  }, [sketchInitialized, !!editLayerRef.current, initialSelectionLoaded]);

  // Event handlers
  const handleSketchCreate: __esri.SketchCreateEventHandler = (event) => {
    if (event.state !== "complete") return;

    // Generate a unique ID for the graphic
    const graphicId = `adhoc-new-${maxFeatureIndex.current}-${crypto.randomUUID()}`;
    const featureName = incrementName(
      "My Feature",
      editLayerRef.current?.graphics || [],
      (g) => g.attributes.name || "",
    );
    event.graphic.attributes = {
      ...(event.graphic.attributes || {}),
      graphicId,
      name: featureName,
      index: maxFeatureIndex.current,
    };
    maxFeatureIndex.current += 1;
    if (event.graphic.geometry) event.graphic.symbol = getSymbolForGeometryType(event.graphic.geometry.type);

    // Add to feature tracking
    setFeatureTracking((prev) => ({
      ...prev,
      [graphicId]: {
        isNew: true,
      },
    }));

    // Add to the index
    if (editLayerRef.current) {
      const newIndex = editLayerRef.current.graphics.length - 1;
      if (newIndex >= 0) {
        graphicAdhocIndex.set(graphicId, newIndex);
      }
    }

    setSelectedTool(SketchTool.Select);
    markFeatureAsChanged?.(graphicId);
  };

  const handleSketchUpdate: __esri.SketchUpdateEventHandler = (event) => {
    if (event.state !== "complete") return;
    // Update tracking for each modified graphic
    event.graphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      const featureId = graphic.attributes?.featureId;
      if (!graphicId) return;
      markFeatureAsChanged?.(graphicId);
      setUndoableActions(0);
      setRedoableActions(0);

      setFeatureTracking((prev) => {
        const currentTracking = prev[graphicId] || {};
        return {
          ...prev,
          [graphicId]: {
            ...currentTracking,
            featureId,
            isModified: true,
          },
        };
      });
    });
  };

  const handleSketchUpdateSelection: (selectedTool: SketchTool) => __esri.SketchUpdateEventHandler =
    (selectedTool: SketchTool) => async (event) => {
      switch (event.state) {
        case "start":
          const graphic = event.graphics[0];
          const graphicId = graphic.attributes?.graphicId;
          if (selectedTool === SketchTool.Select) sketchRef.current?.cancel();
          setSelectedFeatures((prev) => {
            const wasSelected = prev[graphicId];
            editLayerRef.current?.graphics.forEach((g) => {
              if (graphic.geometry)
                graphic.symbol =
                  g.attributes.graphicId != graphicId || wasSelected
                    ? getSymbolForGeometryType(graphic.geometry.type)
                    : getHoverSymbolForGeometryType(graphic.geometry.type);
            });
            return {
              ...prev,
              [graphicId]: !wasSelected,
            };
          });
          break;
        case "active":
          const finishedEvents = [
            "move-stop",
            "reshape-stop",
            "scale-stop",
            "rotate-stop",
            "vertex-add",
            "vertex-remove",
          ];
          if (finishedEvents.includes(event.toolEventInfo.type)) {
            setUndoableActions((prev) => prev + 1);
          } else if (event.toolEventInfo.type === "selection-change") {
            sketchRef.current?.complete();
            sketchRef.current?.update([event.graphics[0]]);
            const graphic = event.graphics[0];
            const graphicId = graphic.attributes?.graphicId;
            setSelectedFeatures((prev) => {
              const wasSelected = prev[graphicId];
              if (graphic.geometry)
                graphic.symbol = wasSelected
                  ? getSymbolForGeometryType(graphic.geometry.type)
                  : getHoverSymbolForGeometryType(graphic.geometry.type);
              return {
                ...prev,
                [graphicId]: !wasSelected,
              };
            });
          }
          break;
      }
    };

  const handleSketchDelete: __esri.SketchDeleteEventHandler = (event) => {
    // Mark deleted features in tracking
    event.graphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      const featureId = graphic.attributes?.featureId;
      if (!graphicId) return;
      markFeatureAsChanged?.(graphicId);
      graphicAdhocIndex.delete(graphicId);

      setFeatureTracking((prev) => {
        const currentTracking = prev[graphicId] || {};

        // If it has a featureId, it exists in the database and should be marked as deleted
        if (featureId) {
          return {
            ...prev,
            [graphicId]: {
              ...currentTracking,
              featureId,
              isDeleted: true,
            },
          };
        }

        // If it's a new feature (not yet saved), just remove from tracking
        if (currentTracking.isNew) {
          const newTracking = { ...prev };
          delete newTracking[graphicId];
          return newTracking;
        }

        return prev;
      });
    });

    // Rebuild the index after deletion
    if (editLayerRef.current) {
      const adhocGraphics = editLayerRef.current.graphics.toArray();
      adhocGraphics.forEach((graphic, index) => {
        const graphicId = graphic.attributes?.graphicId;
        if (graphicId && graphicId.startsWith("adhoc-")) {
          graphicAdhocIndex.set(graphicId, index);
        }
      });
    }
  };

  const handleSketchUndo: __esri.SketchUndoEventHandler = () => {
    setRedoableActions((prev) => prev + 1);
    setUndoableActions((prev) => Math.max(prev - 1, 0));
    readdHandlers(selectedTool);
  };

  const handleSketchRedo: __esri.SketchRedoEventHandler = () => {
    setUndoableActions((prev) => prev + 1);
    setRedoableActions((prev) => Math.max(prev - 1, 0));
    readdHandlers(selectedTool);
  };

  const handleToolSelect = (tool: SketchTool) => {
    if (!sketchRef.current) return;

    setSelectedTool(tool);
    switch (tool) {
      case SketchTool.Polygon:
      case SketchTool.Rectangle:
      case SketchTool.Circle:
        sketchRef.current.create(tool);
        break;
      case SketchTool.Select:
      case SketchTool.Edit:
        // Cancel any active tool and just go into selection mode
        sketchRef.current.cancel();
        readdHandlers(tool);
        break;
      default:
        break;
    }
  };

  const getAdhocFeatures = () => {
    if (!editLayerRef.current) return [];

    return (
      editLayerRef.current.graphics
        .toArray()
        .filter((g) => g.attributes?.graphicId?.startsWith("adhoc-"))
        // Filter out graphics that are marked as deleted but still in the layer
        .filter((g) => {
          const graphicId = g.attributes?.graphicId;
          return !featureTracking[graphicId]?.isDeleted;
        })
    );
  };

  return {
    featureTracking,
    setFeatureTracking,
    selectedTool,
    handleToolSelect,
    maxFeatureIndex,
    readdHandlers,
    getAdhocFeatures,
    sketchCanUndo: undoableActions > 0,
    sketchCanRedo: redoableActions > 0,
  };
}
